import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from "react";
import type { ToastContextValue, ToastItem, ToastOptions, ToastType } from "./types.ts";
import { registerHandlers } from "./toastBus.ts";

// ─── State ────────────────────────────────────────────────────────────────────

type Action =
  | { type: "ADD"; toast: ToastItem }
  | { type: "DISMISS"; id: string }
  | { type: "REMOVE"; id: string }
  | { type: "UPDATE"; id: string; toast: Partial<ToastItem> };

function reducer(state: ToastItem[], action: Action): ToastItem[] {
  switch (action.type) {
    case "ADD":
      return [action.toast, ...state].slice(0, 5); // max 5 toasts
    case "DISMISS":
      return state.map((t) =>
        t.id === action.id ? { ...t, isExiting: true } : t
      );
    case "REMOVE":
      return state.filter((t) => t.id !== action.id);
    case "UPDATE":
      return state.map((t) =>
        t.id === action.id ? { ...t, ...action.toast, isExiting: false } : t
      );
    default:
      return state;
  }
}

// ─── Context ──────────────────────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, dispatch] = useReducer(reducer, []);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const generateId = () =>
    `toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

  const scheduleRemoval = useCallback(
    (id: string, duration: number) => {
      // Clear any existing timer for this id
      const existing = timers.current.get(id);
      if (existing) clearTimeout(existing);

      const dismissTimer = setTimeout(() => {
        dispatch({ type: "DISMISS", id });
        const removeTimer = setTimeout(() => {
          dispatch({ type: "REMOVE", id });
          timers.current.delete(id);
        }, 400); // matches exit animation duration
        timers.current.set(`${id}-remove`, removeTimer);
      }, duration);

      timers.current.set(id, dismissTimer);
    },
    []
  );

  const addToast = useCallback(
    (type: ToastType, message: string, options: ToastOptions = {}): string => {
      const id = options.id ?? generateId();
      const duration = type === "loading" ? Infinity : (options.duration ?? 4000);

      const toast: ToastItem = {
        id,
        type,
        message,
        createdAt: Date.now(),
        ...options,
      };

      dispatch({ type: "ADD", toast });

      if (duration !== Infinity) {
        scheduleRemoval(id, duration);
      }

      return id;
    },
    [scheduleRemoval]
  );

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    dispatch({ type: "DISMISS", id });
    const removeTimer = setTimeout(() => {
      dispatch({ type: "REMOVE", id });
    }, 400);
    timers.current.set(`${id}-remove`, removeTimer);
  }, []);

  const dismissAll = useCallback(() => {
    toasts.forEach((t) => dismiss(t.id));
  }, [toasts, dismiss]);

  const update = useCallback(
    (id: string, type: ToastType, message: string, options: ToastOptions = {}) => {
      const duration = type === "loading" ? Infinity : (options.duration ?? 4000);

      dispatch({
        type: "UPDATE",
        id,
        toast: { type, message, ...options },
      });

      if (duration !== Infinity) {
        scheduleRemoval(id, duration);
      }
    },
    [scheduleRemoval]
  );

  const value: ToastContextValue = {
    toasts,
    success: (msg, opts) => addToast("success", msg, opts),
    error: (msg, opts) => addToast("error", msg, opts),
    warning: (msg, opts) => addToast("warning", msg, opts),
    info: (msg, opts) => addToast("info", msg, opts),
    loading: (msg, opts) => addToast("loading", msg, opts),
    dismiss,
    dismissAll,
    update,
  };

  // Wire up the imperative API once the provider mounts
  useEffect(() => {
    registerHandlers(
      (type, msg, opts) => addToast(type, msg, opts),
      (id) => {
        if (id) dismiss(id);
        else dismissAll();
      },
      update
    );
  }, [addToast, dismiss, dismissAll, update]);

  return (
    <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used inside <ToastProvider>");
  }
  return ctx;
}
